import React, { useState, useMemo } from 'react';
import Modal from './Modal';
import NumericInput from './NumericInput';
import { t, getCurrentLanguage } from './i18n';
import './ThermostatWizard.css';

// Helper to generate slug from name
const slugify = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_')
    .trim();
};

// Dynamic parameter renderer
const renderParameter = (param, value, onChange, wizardMetadata, language) => {
  const label = language === 'fi' ? (param.label_fi || param.label) : param.label;

  switch (param.type) {
    case 'boolean':
      return (
        <label className="wizard-checkbox-label">
          <input
            type="checkbox"
            checked={value ?? param.default ?? false}
            onChange={(e) => onChange(param.name, e.target.checked)}
          />
          {label}
        </label>
      );
    case 'number':
      return (
        <div className="wizard-field">
          <label className="wizard-label">{label}:</label>
          <NumericInput
            value={value ?? param.default ?? 0}
            onChange={(val) => onChange(param.name, parseFloat(val))}
            min={param.min}
            max={param.max}
            step={param.step || 1}
          />
        </div>
      );
    case 'select':
      const options = wizardMetadata?.[param.source] || [];
      return (
        <div className="wizard-field">
          <label className="wizard-label">{label}:</label>
          <select
            className="wizard-select"
            value={value || ''}
            onChange={(e) => onChange(param.name, e.target.value)}
          >
            <option value="">-- {t('wizard.selectOption')} --</option>
            {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      );
    default:
      return null;
  }
};

// Get actor-type-specific fields based on selected heaters
const getActorTypeFields = (selectedHeaterIds, availableHeaters, actorTypeFields) => {
  if (!selectedHeaterIds?.length || !availableHeaters?.length || !actorTypeFields) {
    return [];
  }

  const selectedHeaters = selectedHeaterIds
    .map(id => availableHeaters.find(h => h.id === id))
    .filter(Boolean);

  const actorTypes = [...new Set(selectedHeaters.map(h => h.actor_type).filter(Boolean))];

  // Collect unique fields from all selected actor types
  const fields = [];
  actorTypes.forEach(type => {
    (actorTypeFields[type] || []).forEach(field => {
      if (!fields.find(f => f.name === field.name)) {
        fields.push(field);
      }
    });
  });

  return fields;
};

// Get heaters with actor types
const getHeatersWithActorType = (selectedHeaterIds, availableHeaters) => {
  if (!selectedHeaterIds?.length || !availableHeaters?.length) {
    return [];
  }
  return selectedHeaterIds
    .map(id => availableHeaters.find(h => h.id === id))
    .filter(h => h && h.actor_type);
};

const ThermostatWizard = ({
  isOpen,
  onClose,
  onSave,
  wizardMetadata,
  basicConfigs
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const language = getCurrentLanguage();

  // Step 1: Profile Selection State
  const [profileMode, setProfileMode] = useState('existing'); // 'existing' or 'new'
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [newProfileName, setNewProfileName] = useState('');

  // Step 2: Thermostat Details State
  const [thermostatName, setThermostatName] = useState('');
  const [thermostatType, setThermostatType] = useState('');
  const [location, setLocation] = useState('');
  const [selectedSensorId, setSelectedSensorId] = useState('');
  const [selectedHeaterIds, setSelectedHeaterIds] = useState([]);
  const [actorTypeValues, setActorTypeValues] = useState({});
  const [meteringDevices, setMeteringDevices] = useState({});

  // Step 3: Advanced Settings State
  const [advancedValues, setAdvancedValues] = useState({});

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setProfileMode('existing');
      setSelectedProfileId('');
      setSelectedTemplateId('');
      setNewProfileName('');
      setThermostatName('');
      setThermostatType('');
      setLocation('');
      setSelectedSensorId('');
      setSelectedHeaterIds([]);
      setActorTypeValues({});
      setMeteringDevices({});
      setAdvancedValues({});
      setIsSaving(false);
      setSaveError(null);
      setSaveSuccess(false);
    }
  }, [isOpen]);

  // Memoized metadata values
  const availableSensors = useMemo(() => wizardMetadata?.available_sensors || [], [wizardMetadata]);
  const availableHeaters = useMemo(() => wizardMetadata?.available_heaters || [], [wizardMetadata]);
  const availableMeters = useMemo(() => wizardMetadata?.available_meters || [], [wizardMetadata]);
  const existingProfiles = useMemo(() => wizardMetadata?.existing_profiles || [], [wizardMetadata]);
  const profileTemplates = useMemo(() => wizardMetadata?.profile_templates || {}, [wizardMetadata]);
  const thermostatParameters = useMemo(() => {
    const params = wizardMetadata?.thermostat_parameters || {};
    // Fallback types if not provided by backend
    if (!params.types || params.types.length === 0) {
      params.types = ['Lattialämmitys', 'Sähköpatteri', 'ILP', 'MLP', 'Vesikierto'];
    }
    return params;
  }, [wizardMetadata]);

  // Get actor type fields for selected heaters
  const actorTypeFields = useMemo(() => {
    return getActorTypeFields(
      selectedHeaterIds,
      availableHeaters,
      thermostatParameters?.actor_type_fields
    );
  }, [selectedHeaterIds, availableHeaters, thermostatParameters]);

  // Get heaters that have actor types (for metering device selection)
  const heatersWithActorType = useMemo(() => {
    return getHeatersWithActorType(selectedHeaterIds, availableHeaters);
  }, [selectedHeaterIds, availableHeaters]);

  // Validation for each step
  const isStep1Valid = useMemo(() => {
    if (profileMode === 'existing') {
      return !!selectedProfileId;
    } else {
      return !!selectedTemplateId && !!newProfileName.trim();
    }
  }, [profileMode, selectedProfileId, selectedTemplateId, newProfileName]);

  const isStep2Valid = useMemo(() => {
    const hasName = !!thermostatName.trim();
    const hasType = !!thermostatType;
    const hasSensor = !!selectedSensorId;
    const hasHeaters = selectedHeaterIds.length > 0;

    // Check required actor type fields
    let actorTypeFieldsValid = true;
    actorTypeFields.forEach(field => {
      if (field.required && !actorTypeValues[field.name]) {
        actorTypeFieldsValid = false;
      }
    });

    return hasName && hasType && hasSensor && hasHeaters && actorTypeFieldsValid;
  }, [thermostatName, thermostatType, selectedSensorId, selectedHeaterIds, actorTypeFields, actorTypeValues]);

  // Handle heater checkbox change
  const handleHeaterToggle = (heaterId) => {
    setSelectedHeaterIds(prev => {
      if (prev.includes(heaterId)) {
        return prev.filter(id => id !== heaterId);
      } else {
        return [...prev, heaterId];
      }
    });
  };

  // Handle actor type field change
  const handleActorTypeChange = (fieldName, value) => {
    setActorTypeValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Handle metering device change for specific heater
  const handleMeteringDeviceChange = (heaterId, meterId) => {
    setMeteringDevices(prev => ({
      ...prev,
      [heaterId]: meterId
    }));
  };

  // Handle advanced field change
  const handleAdvancedChange = (fieldName, value) => {
    setAdvancedValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Navigate between steps
  const goToNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Determine actor type from selected heaters
    const selectedHeatersWithActorType = heatersWithActorType;
    const actorType = selectedHeatersWithActorType.length > 0
      ? selectedHeatersWithActorType[0].actor_type
      : undefined;

    // Build thermostat object
    const thermostat = {
      id: slugify(thermostatName),
      name: thermostatName,
      type: thermostatType,
      location: location || undefined,
      sensor_id: selectedSensorId,
      devices: selectedHeaterIds,
    };

    // Add actor type if applicable
    if (actorType) {
      thermostat.actor_type = actorType;
    }

    // Add metering devices if any
    const filteredMeteringDevices = {};
    Object.entries(meteringDevices).forEach(([heaterId, meterId]) => {
      if (meterId) {
        filteredMeteringDevices[heaterId] = meterId;
      }
    });
    if (Object.keys(filteredMeteringDevices).length > 0) {
      thermostat.metering_devices = filteredMeteringDevices;
    }

    // Add actor type specific values
    Object.entries(actorTypeValues).forEach(([key, value]) => {
      if (key !== 'metering_device' && value !== undefined && value !== '') {
        thermostat[key] = value;
      }
    });

    // Add advanced settings
    const advancedParams = thermostatParameters?.advanced || [];
    advancedParams.forEach(param => {
      const value = advancedValues[param.name];
      if (value !== undefined) {
        thermostat[param.name] = value;
      } else if (param.default !== undefined) {
        thermostat[param.name] = param.default;
      }
    });

    // Build the result object
    const result = {
      action: profileMode === 'new' ? 'create_profile' : 'add_thermostat',
      thermostat,
    };

    if (profileMode === 'new') {
      result.profile_id = slugify(newProfileName);
      result.profile_name = newProfileName;
      result.template_id = selectedTemplateId;
    } else {
      result.profile_id = selectedProfileId;
    }

    // Save with loading state
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const response = await onSave(result);

      if (response?.success) {
        setSaveSuccess(true);
        // Auto-close after showing success message
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setSaveError(response?.error || t('wizard.saveError'));
      }
    } catch (error) {
      setSaveError(error.message || t('wizard.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="wizard-steps">
      {[1, 2, 3].map((step) => (
        <div
          key={step}
          className={`wizard-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
        >
          <div className="wizard-step-number">{step}</div>
          <div className="wizard-step-label">
            {step === 1 && t('wizard.stepProfile')}
            {step === 2 && t('wizard.stepDetails')}
            {step === 3 && t('wizard.stepAdvanced')}
          </div>
        </div>
      ))}
    </div>
  );

  // Render Step 1: Profile Selection
  const renderStep1 = () => (
    <div className="wizard-step-content">
      <div className="wizard-radio-group">
        <label className="wizard-radio-label">
          <input
            type="radio"
            name="profileMode"
            value="existing"
            checked={profileMode === 'existing'}
            onChange={() => setProfileMode('existing')}
          />
          {t('wizard.addToExisting')}
        </label>
        <label className="wizard-radio-label">
          <input
            type="radio"
            name="profileMode"
            value="new"
            checked={profileMode === 'new'}
            onChange={() => setProfileMode('new')}
          />
          {t('wizard.createNewProfile')}
        </label>
      </div>

      {profileMode === 'existing' && (
        <div className="wizard-field">
          <label className="wizard-label">{t('wizard.selectProfile')} <span className="wizard-required">*</span></label>
          <select
            className="wizard-select"
            value={selectedProfileId}
            onChange={(e) => setSelectedProfileId(e.target.value)}
          >
            <option value="">-- {t('wizard.selectProfile')} --</option>
            {existingProfiles.map(profile => (
              <option key={profile.profile_id} value={profile.profile_id}>
                {profile.profile_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {profileMode === 'new' && (
        <>
          <div className="wizard-field">
            <label className="wizard-label">{t('wizard.selectTemplate')} <span className="wizard-required">*</span></label>
            <div className="wizard-template-grid">
              {Object.entries(profileTemplates).map(([templateId, template]) => (
                <div
                  key={templateId}
                  className={`wizard-template-card ${selectedTemplateId === templateId ? 'selected' : ''}`}
                  onClick={() => setSelectedTemplateId(templateId)}
                >
                  <div className="wizard-template-name">
                    {language === 'fi' ? template.name : (template.name_en || template.name)}
                  </div>
                  {template.description && (
                    <div className="wizard-template-description">{template.description}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="wizard-field">
            <label className="wizard-label">{t('wizard.profileName')} <span className="wizard-required">*</span></label>
            <input
              type="text"
              className="wizard-input"
              value={newProfileName}
              onChange={(e) => setNewProfileName(e.target.value)}
              placeholder={t('wizard.profileName')}
            />
          </div>
        </>
      )}
    </div>
  );

  // Render Step 2: Thermostat Details
  const renderStep2 = () => (
    <div className="wizard-step-content">
      <div className="wizard-field">
        <label className="wizard-label">{t('wizard.thermostatName')} <span className="wizard-required">*</span></label>
        <input
          type="text"
          className="wizard-input"
          value={thermostatName}
          onChange={(e) => setThermostatName(e.target.value)}
          placeholder={t('wizard.thermostatName')}
        />
      </div>

      <div className="wizard-field">
        <label className="wizard-label">{t('wizard.thermostatType')} <span className="wizard-required">*</span></label>
        <select
          className="wizard-select"
          value={thermostatType}
          onChange={(e) => setThermostatType(e.target.value)}
        >
          <option value="">-- {t('wizard.thermostatType')} --</option>
          {(thermostatParameters?.types || []).map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      <div className="wizard-field">
        <label className="wizard-label">{t('wizard.location')} <span className="wizard-optional">({t('wizard.optional')})</span></label>
        <input
          type="text"
          className="wizard-input"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder={t('wizard.location')}
        />
      </div>

      <div className="wizard-field">
        <label className="wizard-label">{t('wizard.selectSensor')} <span className="wizard-required">*</span></label>
        <select
          className="wizard-select"
          value={selectedSensorId}
          onChange={(e) => setSelectedSensorId(e.target.value)}
        >
          <option value="">-- {t('wizard.selectSensor')} --</option>
          {availableSensors.map(sensor => (
            <option key={sensor.id} value={sensor.id}>
              {sensor.name} ({sensor.platform})
            </option>
          ))}
        </select>
      </div>

      <div className="wizard-field">
        <label className="wizard-label">{t('wizard.selectHeaters')} <span className="wizard-required">*</span></label>
        <div className="wizard-checkbox-group">
          {availableHeaters.map(heater => (
            <label key={heater.id} className="wizard-checkbox-label">
              <input
                type="checkbox"
                checked={selectedHeaterIds.includes(heater.id)}
                onChange={() => handleHeaterToggle(heater.id)}
              />
              {heater.name} ({heater.platform})
            </label>
          ))}
        </div>
      </div>

      {/* Dynamic actor type fields */}
      {actorTypeFields.length > 0 && (
        <div className="wizard-section">
          <h4 className="wizard-section-title">{t('wizard.deviceSpecificSettings')}</h4>
          {actorTypeFields.map(field => {
            // Handle metering device specially - show per heater
            if (field.name === 'metering_device' && field.type === 'select') {
              return (
                <div key={field.name} className="wizard-metering-devices">
                  <label className="wizard-label">
                    {language === 'fi' ? (field.label_fi || field.label) : field.label}
                    {field.required && <span className="wizard-required">*</span>}
                  </label>
                  {heatersWithActorType.map(heater => (
                    <div key={heater.id} className="wizard-metering-device-row">
                      <span className="wizard-heater-name">{heater.name}:</span>
                      <select
                        className="wizard-select wizard-select-small"
                        value={meteringDevices[heater.id] || ''}
                        onChange={(e) => handleMeteringDeviceChange(heater.id, e.target.value)}
                      >
                        <option value="">-- {t('wizard.selectOption')} --</option>
                        {availableMeters.map(meter => (
                          <option key={meter.id} value={meter.id}>{meter.name}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              );
            }

            return (
              <div key={field.name}>
                {renderParameter(
                  field,
                  actorTypeValues[field.name],
                  handleActorTypeChange,
                  wizardMetadata,
                  language
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Render Step 3: Advanced Settings
  const renderStep3 = () => {
    const advancedParams = thermostatParameters?.advanced || [];

    return (
      <div className="wizard-step-content">
        <h4 className="wizard-section-title">{t('wizard.advancedSettings')}</h4>
        <p className="wizard-help-text">{t('wizard.advancedSettingsHelp')}</p>

        {advancedParams.map(param => {
          // Check depends_on condition
          if (param.depends_on) {
            const dependsOnValue = advancedValues[param.depends_on];
            if (!dependsOnValue) {
              return null;
            }
          }

          return (
            <div key={param.name}>
              {renderParameter(
                param,
                advancedValues[param.name],
                handleAdvancedChange,
                wizardMetadata,
                language
              )}
            </div>
          );
        })}

        {advancedParams.length === 0 && (
          <p className="wizard-no-data">{t('wizard.noAdvancedSettings')}</p>
        )}
      </div>
    );
  };

  // Render navigation buttons
  const renderNavigation = () => (
    <div className="wizard-navigation">
      <div>
        {currentStep > 1 && !saveSuccess && (
          <button
            type="button"
            className="wizard-btn wizard-btn-secondary"
            onClick={goToPreviousStep}
            disabled={isSaving}
          >
            {t('wizard.back')}
          </button>
        )}
      </div>
      <div>
        {currentStep < 3 ? (
          <button
            type="button"
            className="wizard-btn wizard-btn-primary"
            onClick={goToNextStep}
            disabled={currentStep === 1 && !isStep1Valid || currentStep === 2 && !isStep2Valid}
          >
            {t('wizard.next')}
          </button>
        ) : (
          <button
            type="button"
            className="wizard-btn wizard-btn-primary"
            onClick={handleSubmit}
            disabled={isSaving || saveSuccess}
          >
            {isSaving ? t('wizard.saving') : t('wizard.create')}
          </button>
        )}
      </div>
    </div>
  );

  if (!wizardMetadata) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('wizard.createThermostat')}
      size="large"
    >
      <div className="wizard-container">
        {renderStepIndicator()}

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}

        {/* Success message */}
        {saveSuccess && (
          <div className="wizard-message wizard-message-success">
            ✓ {t('wizard.saveSuccess')}
          </div>
        )}

        {/* Error message */}
        {saveError && (
          <div className="wizard-message wizard-message-error">
            ✗ {saveError}
          </div>
        )}

        {renderNavigation()}
      </div>
    </Modal>
  );
};

export default ThermostatWizard;
